import { useState, useEffect } from 'react';
import styles from './bulkUploadModal.module.scss';
import { IoMdClose } from 'react-icons/io';
import ButtonShared from '../Button';
import { FiUpload } from 'react-icons/fi';
import { MdOutlineFileDownload } from 'react-icons/md';
import { FiFile, FiX } from 'react-icons/fi';
import BulkUploadImagesModal from '../BulkUploadImagesModal/BulkUploadImagesModal';
import { BulkUploadModalProps } from '../../interfaces/components';

const BulkUploadModal = ({
  isOpen,
  onClose,
  vehicleType,
  selectedPublicationMethod,
  onUploadComplete,
  onCloseAll,
  massiveCharge,
  downloadUsedVehicleTemplate,
  downloadNewVehicleTemplate,
  bulkUploadVehicles,
  bulkUploadImages,
  validateUploadStatus,
  cleanMassiveCharge,
  selectedFile,
  setSelectedFile,
  fileError,
  setFileError,
  lineCount,
  setLineCount,
  showImagesModal,
  setShowImagesModal,
  confirmUploadProcess,
  isUploading,
  setIsUploading,
  bulkUploadImagesStorage
}: BulkUploadModalProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [replacement, setReplacement] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentArchiveId, setCurrentArchiveId] = useState<string | null>(null);
  const [processingMessage, setProcessingMessage] = useState('Procesando archivo...');

  const summaryData = {
    totalCount: lineCount ? Math.min(lineCount - 1, 25) : 25,
    identifiedCount: lineCount ? Math.floor((lineCount - 1) * 0.8) : 25,
    incompleteCount: lineCount ? Math.floor((lineCount - 1) * 0.15) : 0,
    notImportedCount: lineCount ? Math.floor((lineCount - 1) * 0.05) : 0,
    errorFileName: 'listado-no-reconocidos.xlsx'
  };

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    const checkValidationStatus = async (archiveId: string) => {
      try {
        const validationResult = await validateUploadStatus(archiveId);
        
        const validationStatus = validationResult?.data?.process_publications?.status;
        const totalRecords = validationResult?.data?.process_publications?.total_records;
        
        if (validationStatus && validationStatus.toLowerCase().includes('completed')) {
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
          
          setIsProcessing(false);
          
          if (totalRecords > 0) {
            setShowImagesModal(true);
          } else {
            setFileError('Error: No se encontraron registros. Por favor revisa el archivo e intenta de nuevo.');
          }
          
          setCurrentArchiveId(null);
        } else if (validationStatus === 'failed') {
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
          
          setIsProcessing(false);
          setFileError('Error: La validación del archivo falló. Por favor revisa el archivo e intenta de nuevo.');
          setCurrentArchiveId(null);
        } else {
          setProcessingMessage(`Procesando archivo...`);
        }
      } catch (error) {
      }
    };

    if (currentArchiveId && isProcessing) {
      checkValidationStatus(currentArchiveId);
      
      intervalId = setInterval(() => {
        checkValidationStatus(currentArchiveId);
      }, 10000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [currentArchiveId, isProcessing, validateUploadStatus]);

  const resetInternalState = () => {
    setSelectedFile(null);
    setFileError('');
    setShowImagesModal(false);
    setIsProcessing(false);
    setCurrentArchiveId(null);
    setProcessingMessage('Procesando archivo...');

    const fileInput = document.getElementById('file-upload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleClose = () => {
    resetInternalState();
    onClose();
  };

  const handleCloseAll = () => {
    resetInternalState();
    onCloseAll();
  };

  const handleDownloadTemplate = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    try {
      let response;
      if (vehicleType === 'used') {
        response = await downloadUsedVehicleTemplate();
      } else if (vehicleType === 'new') {
        response = await downloadNewVehicleTemplate();
      }

      if (response?.parameter?.value) {
        const downloadUrl = response?.parameter?.value;

        const link = document.createElement('a');
        link.href = downloadUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('❌ Error downloading template:', error);
    }
  };

  if (!isOpen) return null;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (files && files.length > 0) {
      const file = files[0];

      if (file.name.endsWith('.csv') || file.name.endsWith('.xlsx')) {
        setSelectedFile(file);
        setFileError('');
      } else {
        setFileError('Por favor sube un archivo .csv o .xlsx');
        setSelectedFile(null);
      }
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileError('');
    const fileInput = document.getElementById('file-upload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.currentTarget.contains(e.relatedTarget as Node)) {
      return;
    }
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];

      if (file.name.endsWith('.csv') || file.name.endsWith('.xlsx')) {
        setSelectedFile(file);
        setFileError('');
      } else {
        setFileError('Por favor sube un archivo .csv o .xlsx');
        setSelectedFile(null);
      }
    }
  };

  const handleUpload = async () => {
    if (selectedFile && !isUploading) {
      setIsUploading(true);
      setIsProcessing(true);
      setProcessingMessage('Subiendo archivo...');
      
      try {
        const result = await bulkUploadVehicles(selectedFile, vehicleType, replacement);

        if (result && result.success !== false) {
          const archiveId = result?.data?.process_publication?.processPublication?.id;
          
          if (archiveId) {
            setCurrentArchiveId(archiveId);
            setProcessingMessage('Validando archivo...');
          } else {
            setFileError('Error: No se pudo obtener el ID del archivo');
            setIsProcessing(false);
          }
        } else {
          setFileError('Error al subir el archivo. Por favor intenta de nuevo.');
          setIsProcessing(false);
        }

      } catch (error) {
        setFileError('Error al subir el archivo. Por favor intenta de nuevo.');
        setIsProcessing(false);
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleImagesModalClose = () => {
    setShowImagesModal(false);
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContainer}>
        <button className={styles.closeButton} onClick={handleCloseAll}>
          <IoMdClose />
        </button>

        <div className={styles.modalHeader}>
          <h2>Importa un lote de vehículos</h2>
        </div>

        <div className={styles.modalContent}>
          <p className={styles.instructions}>
            Sube un archivo con el listado de los vehículos que quieres publicar y sus características, respetando el formato del archivo modelo.
            <br />
            <p className={styles.subInstructions}>En el siguiente paso, podrás subir las fotos de cada auto.</p>
          </p>

          <div className={styles.downloadTemplateWrapper}>
            <a href="#" className={styles.templateLink} onClick={handleDownloadTemplate}>
              <MdOutlineFileDownload size={20} />
              <span>Archivo modelo</span>
            </a>
          </div>

          {isProcessing ? (
            <div className={styles.processingContainer}>
              <div className={styles.processingContent}>
                <div className={styles.spinner}></div>
                <p className={styles.processingMessage}>{processingMessage}</p>
                <p className={styles.processingSubtext}>Esto puede tomar algunos momentos. No cierres esta ventana.</p>
              </div>
            </div>
          ) : !selectedFile ? (
            <div 
              className={`${styles.uploadBox} ${isDragging ? styles.uploadBoxDragging : ''}`}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className={styles.uploadContent}>
                <input
                  type="file"
                  id="file-upload"
                  accept=".xlsx,.csv"
                  onChange={handleFileChange}
                  className={styles.fileInput}
                />
                <label htmlFor="file-upload" className={styles.uploadLabel}>
                  <FiUpload size={24} />
                  <span>Subir archivo o arrastra aquí</span>
                </label>
                <p className={styles.formatText}>
                  Formato admitido: .csv o .xlsx
                </p>
              </div>
            </div>
          ) : (
            <div className={styles.selectedFileContainer}>
              <div className={styles.selectedFileContent}>
                <div className={styles.fileIconContainer}>
                  <FiFile size={20} />
                </div>
                <span className={styles.fileName}>{selectedFile.name}</span>
                <button
                  className={styles.removeFileButton}
                  onClick={handleRemoveFile}
                  aria-label="Eliminar archivo"
                >
                  <FiX size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Solo mostrar error si no está procesando y hay un error */}
          {fileError && !isProcessing && (
            <div className={styles.errorMessage}>
              <p>{fileError}</p>
            </div>
          )}
        </div>

        <div className={styles.replacementContainer}>
          <div className={styles.radioGroup}>
            <p className={styles.radioTitle}>Indica qué hacer si el archivo incluye autos con patentes que ya cargaste:</p>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                name="replacementOption"
                value="new"
                checked={replacement}
                onChange={() => setReplacement(true)}
              />
              <span>Reemplazar existentes: La información se actualizará con la de este archivo.</span>
            </label>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                name="replacementOption"
                value="replace"
                checked={!replacement}
                onChange={() => setReplacement(false)}
              />
              <span>Mantener información actual: Los autos ya registrados se conservarán y no se reemplazarán.</span>
            </label>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <ButtonShared
            onPress={handleClose}
            title="Volver"
            tertiary
            className={styles.cancelButton}
            id="cancelBulkUploadButton"
            size={"small"}
          />
          <ButtonShared
            onPress={handleUpload}
            title={isUploading ? "Subiendo..." : "Continuar"}
            primary
            disabled={!selectedFile || isUploading || isProcessing}
            className={styles.continueButton}
            size={"small"}
            loading={isUploading}
          />
        </div>
      </div>

      <BulkUploadImagesModal
        isOpen={showImagesModal}
        onClose={handleImagesModalClose}
        onCloseAll={onCloseAll}
        vehicleType={vehicleType}
        selectedPublicationMethod={selectedPublicationMethod}
        onUploadComplete={onUploadComplete}
        massiveCharge={massiveCharge}
        bulkUploadImages={bulkUploadImages}
        bulkUploadImagesStorage={bulkUploadImagesStorage}
        validateUploadStatus={validateUploadStatus}
        cleanMassiveCharge={cleanMassiveCharge}
        confirmUploadProcess={confirmUploadProcess}
      />
    </div>
  );
};

export default BulkUploadModal;
